import React, { useState } from "react";
import { Nav } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { FaTachometerAlt, FaShieldAlt, FaNetworkWired, FaTimes, FaHandshake, FaServer, FaChevronLeft, FaChevronRight, FaHeadset, FaHdd } from "react-icons/fa";
import vIcon from "../../assets/logo192.png";

import {ROUTES} from "../../routes.js";
import { getClientHomeRoute, isEngagementsOnlyClient } from "../../utils/clientMsp";

/* ── Freshdesk-style tooltip nav item ─────────────────────────────────────── */
const SideNavItem = ({ to, icon: Icon, label, active, collapsed, onClick, external, disabled, tooltip }) => {
  const [hovered, setHovered] = useState(false);

  const linkProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  const LinkComponent = disabled ? "div" : Link;

  return (
    <Nav.Item className="mb-2" style={{ position: "relative" }}>
      <Nav.Link
        as={LinkComponent}
        to={disabled ? undefined : to}
        active={active}
        onClick={disabled ? undefined : onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title={tooltip}
        disabled={disabled}
        {...linkProps}
        style={{
          color: disabled ? "var(--color-text-light)" : active ? "var(--color-primary)" : "var(--color-text-light)",
          background: active ? "rgba(111,66,193,0.1)" : "transparent",
          borderRadius: 12,
          padding: collapsed ? "10px" : "10px 14px",
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-start",
          alignItems: "center",
          transition: "background 0.15s, color 0.15s",
          position: "relative",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.65 : 1,
        }}
      >
        <Icon size={18} style={{ marginRight: collapsed ? 0 : 8, flexShrink: 0 }} />
        {!collapsed && <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>}
      </Nav.Link>

      {/* Floating label tooltip — only when collapsed & hovered */}
      {collapsed && hovered && (
        <div
          style={{
            position: "absolute",
            left: "calc(100% + 14px)",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            zIndex: 9999,
            background: "var(--color-bg-card)",
            color: "var(--color-text)",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
          }}
        >
          {/* Left arrow border */}
          <div style={{
            position: "absolute",
            left: -7,
            top: "50%",
            transform: "translateY(-50%)",
            width: 0, height: 0,
            borderTop: "6px solid transparent",
            borderBottom: "6px solid transparent",
            borderRight: "6px solid var(--color-border)",
          }} />
          {/* Left arrow fill */}
          <div style={{
            position: "absolute",
            left: -6,
            top: "50%",
            transform: "translateY(-50%)",
            width: 0, height: 0,
            borderTop: "6px solid transparent",
            borderBottom: "6px solid transparent",
            borderRight: "6px solid var(--color-bg-card)",
          }} />
          {label}
        </div>
      )}
    </Nav.Item>
  );
};

const Sidebar = ({ collapsed, open, onClose, onToggleCollapse, mode, notificationsOpen, onCloseNotifications, selectedClient }) => {
  const isMobile = window.innerWidth <= 992;
  const location = useLocation();

  const isClientMode = mode === "client";
  const helpdeskEnabled = Boolean(
    selectedClient?.hasHelpdesk ||
    selectedClient?.has_freshservice ||
    selectedClient?.freshservice_workspace_id
  );
  const showHelpdeskItem = !isClientMode || Boolean(selectedClient);

  // Determine route prefix
  const isStaff = mode === "superadmin";
  const overviewPath = (isStaff ? ROUTES.SUPERADMIN_OVERVIEW : ROUTES.CLIENT_OVERVIEW);
  const securityPath = (isStaff ? ROUTES.SUPERADMIN_SECURITY : ROUTES.CLIENT_SECURITY);
  const assetsPath = (isStaff ? ROUTES.SUPERADMIN_ASSETS : ROUTES.CLIENT_ASSETS);
  const networkPath = (isStaff ? ROUTES.SUPERADMIN_NETWORK : ROUTES.CLIENT_NETWORK);
  const engagementPath = (isStaff ? ROUTES.SUPERADMIN_ENGAGEMENT : ROUTES.CLIENT_ENGAGEMENT);
  const helpdeskPath = (isStaff ? ROUTES.SUPERADMIN_HELPDESK : ROUTES.CLIENT_HELPDESK);
  const isActive = (path) => location.pathname === path;
  const engagementsOnly = isEngagementsOnlyClient(selectedClient);
  const nonMspNavTooltip = "Currently unavailable";

  const handleNavClick = () => {
    if (notificationsOpen && onCloseNotifications) onCloseNotifications();
    if (isMobile && onClose) onClose();
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 998,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className="sidebar"
        style={{
          width: collapsed ? 78 : 240,
          height: "100vh",
          position: "fixed",
          left: open ? 0 : isMobile ? "-260px" : 0,
          top: 0,
          padding: collapsed ? "4px 4px 20px 4px" : "4px 14px 20px 14px",
          background: "var(--color-bg-sidebar)",
          borderRight: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-sm)",
          transition: "all 0.25s ease",
          zIndex: 999,
        }}
      >
        {/* Close (Mobile) */}
        {isMobile && (
          <div className="d-flex justify-content-end mb-3">
            <button
              onClick={onClose}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--color-text)",
                fontSize: 20,
              }}
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Logo */}
        <div
          style={{
            marginBottom: 30,
            padding: collapsed ? "0" : "0 6px 4px 6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: collapsed ? "56px" : "80px",
            width: "100%",
            overflow: "hidden",
          }}
          className="logo-container"
        >
          {collapsed ? (
            /* "V" brand icon when rail is collapsed */
            <img
              src={vIcon}
              alt="Versetal"
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 8,
                transition: "all 0.25s ease",
              }}
            />
          ) : (
            /* Full logo when expanded */
            <img
              src="/static/images/logo.png"
              alt="Versetal OneView"
              onError={(e) => { e.target.style.display = "none"; }}
              style={{
                width: "100%",
                maxHeight: 80,
                objectFit: "contain",
                filter: "var(--logo-filter, none)",
                transition: "all 0.25s ease",
                display: "block",
              }}
            />
          )}
        </div>

        {/* Menu */}
        <div
          style={{
            background: "var(--color-bg-sidebar-content)",
            borderRadius: "12px",
            padding: collapsed ? "8px 4px" : "12px 8px",
            marginTop: "0",
          }}
        >
          <Nav className="flex-column">
            <SideNavItem to={overviewPath}    icon={FaTachometerAlt} label="Overview"       active={isActive(overviewPath)}    collapsed={collapsed} onClick={engagementsOnly ? undefined : handleNavClick} disabled={engagementsOnly} tooltip={engagementsOnly ? nonMspNavTooltip : undefined} />
            <SideNavItem to={securityPath}    icon={FaShieldAlt}     label="Security"       active={isActive(securityPath)}    collapsed={collapsed} onClick={engagementsOnly ? undefined : handleNavClick} disabled={engagementsOnly} tooltip={engagementsOnly ? nonMspNavTooltip : undefined} />
            <SideNavItem to={assetsPath}      icon={FaHdd}           label="Assets"         active={isActive(assetsPath)}      collapsed={collapsed} onClick={engagementsOnly ? undefined : handleNavClick} disabled={engagementsOnly} tooltip={engagementsOnly ? nonMspNavTooltip : undefined} />
            {/* Network tab temporarily hidden per team request. Route/page code retained. */}
            {false && (
              <SideNavItem to={networkPath}   icon={FaNetworkWired}  label="Network"        active={isActive(networkPath)}     collapsed={collapsed} onClick={engagementsOnly ? undefined : handleNavClick} disabled={engagementsOnly} tooltip={engagementsOnly ? nonMspNavTooltip : undefined} />
            )}
            <SideNavItem to={engagementPath}  icon={FaHandshake}     label="Engagements"    active={isActive(engagementPath)}  collapsed={collapsed} onClick={handleNavClick} />
            {showHelpdeskItem && (
              <SideNavItem
                to={helpdeskPath}
                icon={FaHeadset}
                label="Helpdesk"
                active={isActive(helpdeskPath)}
                collapsed={collapsed}
                onClick={helpdeskEnabled && !engagementsOnly ? handleNavClick : undefined}
                disabled={!helpdeskEnabled || engagementsOnly}
                tooltip={
                  engagementsOnly
                    ? nonMspNavTooltip
                    : !selectedClient
                    ? "Select a client to view Helpdesk."
                    : helpdeskEnabled ? undefined : "This client does not have Helpdesk enabled."
                }
              />
            )}
            {isStaff && (
              <SideNavItem to={ROUTES.ADMIN}  icon={FaServer}        label="Administration" active={false}                     collapsed={collapsed} onClick={handleNavClick} external />
            )}
          </Nav>
        </div>

        {/* Collapse Toggle — pinned to bottom */}
        {!isMobile && (
          <div
            onClick={onToggleCollapse}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            style={{
              position: "absolute",
              bottom: 20,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-end",
              paddingRight: collapsed ? 0 : 14,
              cursor: "pointer",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s",
                boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--color-bg-hover)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--color-bg-card)"}
            >
              {collapsed
                ? <FaChevronRight size={12} color="var(--color-text-light)" />
                : <FaChevronLeft  size={12} color="var(--color-text-light)" />
              }
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
